import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  MessageCircle, 
  Search, 
  Filter, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  User,
  Mail,
  Calendar,
  Reply,
  Send,
  Star,
  Archive,
  Trash2,
  MoreHorizontal,
  Tag,
  Eye,
  EyeOff,
  Plus,
  X
} from 'lucide-react';

interface SupportMessage {
  id: string;
  subject: string;
  message: string;
  userName: string;
  userEmail: string;
  userId: string;
  status: 'új' | 'folyamatban' | 'megoldva' | 'lezárva';
  priority: 'alacsony' | 'közepes' | 'magas' | 'sürgős';
  createdAt: string;
  updatedAt: string;
  assignedTo?: string;
  category: string;
  tags: string[];
  replies: Reply[];
  isRead: boolean;
  rating?: number;
}

interface Reply {
  id: string;
  message: string;
  isFromAdmin: boolean;
  adminName?: string;
  timestamp: string;
}

const Support: React.FC = () => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<SupportMessage | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('mind');
  const [priorityFilter, setPriorityFilter] = useState<string>('mind');
  const [replyText, setReplyText] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    userName: '',
    userEmail: '',
    priority: 'közepes' as 'alacsony' | 'közepes' | 'magas' | 'sürgős',
    category: 'Általános'
  });

  // Firebase Firestore loading with real-time updates (admin data)
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'support_messages'), 
      (snapshot) => {
        const messagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Convert Firebase timestamps to ISO strings if needed
          createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || doc.data().createdAt,
          updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || doc.data().updatedAt,
          replies: doc.data().replies?.map((reply: any) => ({
            ...reply,
            timestamp: reply.timestamp?.toDate?.()?.toISOString() || reply.timestamp
          })) || []
        })) as SupportMessage[];
        
        // Sort by newest first
        messagesData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setMessages(messagesData);
      },
      (error) => {
        console.error('Error fetching support messages:', error);
        // Don't show error to user unless it's critical
        if (error.code !== 'permission-denied') {
          toast.error('Hiba történt az üzenetek betöltésekor');
        }
      }
    );

    return () => {
      try {
        unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing from support messages:', error);
      }
    };
  }, []);

  const filteredMessages = messages.filter(message => {
    const matchesSearch = message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         message.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         message.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'mind' || message.status === statusFilter;
    const matchesPriority = priorityFilter === 'mind' || message.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'új': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'folyamatban': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'megoldva': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'lezárva': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'alacsony': return 'bg-gray-100 text-gray-800';
      case 'közepes': return 'bg-blue-100 text-blue-800';
      case 'magas': return 'bg-orange-100 text-orange-800';
      case 'sürgős': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusChange = async (messageId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'support_messages', messageId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => prev ? { ...prev, status: newStatus as any } : null);
      }
      
      toast.success('Státusz sikeresen frissítve!');
    } catch (error) {
      console.error('Hiba a státusz frissítésekor:', error);
      toast.error('Hiba történt a státusz frissítésekor');
    }
  };

  const handleReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;

    setLoading(true);
    
    try {
      const newReply: Reply = {
        id: `r_${Date.now()}`,
        message: replyText,
        isFromAdmin: true,
        adminName: currentUser?.displayName || currentUser?.email || 'Admin',
        timestamp: new Date().toISOString()
      };

      const updatedReplies = [...selectedMessage.replies, newReply];

      await updateDoc(doc(db, 'support_messages', selectedMessage.id), {
        replies: updatedReplies,
        status: 'folyamatban',
        updatedAt: serverTimestamp()
      });

      setSelectedMessage(prev => prev ? {
        ...prev,
        replies: updatedReplies,
        status: 'folyamatban' as any
      } : null);

      setReplyText('');
      setShowReplyForm(false);
      toast.success('Válasz sikeresen elküldve!');
    } catch (error) {
      console.error('Hiba a válasz küldésekor:', error);
      toast.error('Hiba történt a válasz küldésekor');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, isRead: true } : msg
    ));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('hu-HU');
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim() || !newTicket.userName.trim() || !newTicket.userEmail.trim()) {
      toast.error('Minden mező kitöltése kötelező');
      return;
    }

    setLoading(true);

    try {
      const ticket = {
        subject: newTicket.subject,
        message: newTicket.message,
        userName: newTicket.userName,
        userEmail: newTicket.userEmail,
        userId: `manual_${Date.now()}`,
        status: 'új',
        priority: newTicket.priority,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        category: newTicket.category,
        tags: [],
        replies: [],
        isRead: false
      };

      await addDoc(collection(db, 'support_messages'), ticket);
      
      // Reset form
      setNewTicket({
        subject: '',
        message: '',
        userName: '',
        userEmail: '',
        priority: 'közepes',
        category: 'Általános'
      });
      
      setShowNewTicketModal(false);
      toast.success('Ticket sikeresen létrehozva!');
    } catch (error) {
      console.error('Hiba a ticket létrehozásakor:', error);
      toast.error('Hiba történt a ticket létrehozásakor');
    } finally {
      setLoading(false);
    }
  };

  const resetNewTicketForm = () => {
    setNewTicket({
      subject: '',
      message: '',
      userName: '',
      userEmail: '',
      priority: 'közepes',
      category: 'Általános'
    });
    setShowNewTicketModal(false);
  };

  const unreadCount = messages.filter(msg => !msg.isRead).length;
  const newCount = messages.filter(msg => msg.status === 'új').length;
  const inProgressCount = messages.filter(msg => msg.status === 'folyamatban').length;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Ügyfélszolgálat
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Játékosok üzeneteinek kezelése és támogatás
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-4">
            <button
              onClick={() => setShowNewTicketModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Új ticket</span>
            </button>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Olvasatlan:</span>
              <span className="px-2 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                {unreadCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Összes üzenet</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{messages.length}</p>
            </div>
            <MessageCircle className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Új üzenetek</p>
              <p className="text-2xl font-bold text-blue-600">{newCount}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Folyamatban</p>
              <p className="text-2xl font-bold text-yellow-600">{inProgressCount}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Megoldva ma</p>
              <p className="text-2xl font-bold text-green-600">5</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Messages List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md">
            {/* Search and Filters */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Keresés üzenetekben..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="mind">Minden státusz</option>
                  <option value="új">Új</option>
                  <option value="folyamatban">Folyamatban</option>
                  <option value="megoldva">Megoldva</option>
                  <option value="lezárva">Lezárva</option>
                </select>

                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="mind">Minden prioritás</option>
                  <option value="alacsony">Alacsony</option>
                  <option value="közepes">Közepes</option>
                  <option value="magas">Magas</option>
                  <option value="sürgős">Sürgős</option>
                </select>
              </div>
            </div>

            {/* Messages */}
            <div className="max-h-96 overflow-y-auto">
              {filteredMessages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (!message.isRead) markAsRead(message.id);
                  }}
                  className={`p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    selectedMessage?.id === message.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  } ${!message.isRead ? 'bg-blue-25 dark:bg-blue-900/10' : ''}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {!message.isRead && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                      <h3 className="font-medium text-gray-900 dark:text-white text-sm">
                        {message.subject}
                      </h3>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(message.priority)}`}>
                      {message.priority}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {message.userName}
                  </p>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-500 mb-2 line-clamp-2">
                    {message.message}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(message.status)}`}>
                      {message.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(message.createdAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2">
          {selectedMessage ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {selectedMessage.subject}
                    </h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <User className="h-4 w-4" />
                        <span>{selectedMessage.userName}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Mail className="h-4 w-4" />
                        <span>{selectedMessage.userEmail}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(selectedMessage.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedMessage.status}
                      onChange={(e) => handleStatusChange(selectedMessage.id, e.target.value)}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="új">Új</option>
                      <option value="folyamatban">Folyamatban</option>
                      <option value="megoldva">Megoldva</option>
                      <option value="lezárva">Lezárva</option>
                    </select>
                  </div>
                </div>

                {/* Tags and Category */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedMessage.category}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {selectedMessage.tags.map(tag => (
                      <span key={tag} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                    {selectedMessage.message}
                  </p>
                </div>
              </div>

              {/* Replies */}
              <div className="p-6 max-h-64 overflow-y-auto">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                  Válaszok ({selectedMessage.replies.length})
                </h3>
                
                <div className="space-y-4">
                  {selectedMessage.replies.map((reply) => (
                    <div key={reply.id} className={`flex ${reply.isFromAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                        reply.isFromAdmin 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                        <div className="mt-2 text-xs opacity-75 flex items-center justify-between">
                          <span>{reply.isFromAdmin ? reply.adminName : selectedMessage.userName}</span>
                          <span>{formatDate(reply.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reply Form */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                {!showReplyForm ? (
                  <button
                    onClick={() => setShowReplyForm(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Reply className="h-4 w-4" />
                    <span>Válasz írása</span>
                  </button>
                ) : (
                  <div>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Írja be a válaszát..."
                      rows={4}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <div className="mt-3 flex items-center space-x-3">
                      <button
                        onClick={handleReply}
                        disabled={loading || !replyText.trim()}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send className="h-4 w-4" />
                        <span>{loading ? 'Küldés...' : 'Küldés'}</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowReplyForm(false);
                          setReplyText('');
                        }}
                        className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                      >
                        Mégse
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 text-center">
              <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Válasszon egy üzenetet
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Kattintson egy üzenetre a bal oldali listából a részletek megtekintéséhez.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={resetNewTicketForm}></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Új támogatási ticket létrehozása
                  </h3>
                  <button
                    onClick={resetNewTicketForm}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* User Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Felhasználó neve
                      </label>
                      <input
                        type="text"
                        value={newTicket.userName}
                        onChange={(e) => setNewTicket(prev => ({ ...prev, userName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Pl: Kovács János"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email cím
                      </label>
                      <input
                        type="email"
                        value={newTicket.userEmail}
                        onChange={(e) => setNewTicket(prev => ({ ...prev, userEmail: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="felhasznalo@email.com"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tárgy
                    </label>
                    <input
                      type="text"
                      value={newTicket.subject}
                      onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Pl: Bejelentkezési probléma"
                    />
                  </div>

                  {/* Category and Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Kategória
                      </label>
                      <select
                        value={newTicket.category}
                        onChange={(e) => setNewTicket(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="Általános">Általános</option>
                        <option value="Bejelentkezés">Bejelentkezés</option>
                        <option value="Fizetés">Fizetés</option>
                        <option value="Technikai">Technikai</option>
                        <option value="Játék">Játék</option>
                        <option value="Adatvédelem">Adatvédelem</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Prioritás
                      </label>
                      <select
                        value={newTicket.priority}
                        onChange={(e) => setNewTicket(prev => ({ ...prev, priority: e.target.value as any }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="alacsony">Alacsony</option>
                        <option value="közepes">Közepes</option>
                        <option value="magas">Magas</option>
                        <option value="sürgős">Sürgős</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Üzenet
                    </label>
                    <textarea
                      rows={4}
                      value={newTicket.message}
                      onChange={(e) => setNewTicket(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Írja le a problémát vagy kérdést részletesen..."
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleCreateTicket}
                  disabled={loading || !newTicket.subject.trim() || !newTicket.message.trim() || !newTicket.userName.trim() || !newTicket.userEmail.trim()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Létrehozás...' : 'Ticket létrehozása'}
                </button>
                <button
                  onClick={resetNewTicketForm}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support; 